/**
 * The TOT-DASH rate ladder on screen (Pedro 2026-10-22), shared by View portfolio and View
 * security (SEC-LADDER: "bring the view security into line"). Rung 1 (the custodian's own rates
 * that date) needs nothing here. For each currency a custodian lacks that day, a yellow notice
 * (app-language §2: a warning, nothing blocked) offers what exists: rung 2, its last earlier
 * rate or another custodian's rate that date; rung 3, a rate typed by the user ("This view
 * only": never stored, gone on reload). Until one is chosen, rung 4: the portfolios concerned
 * are left out of the totals and %, and named. Rates in use that are not the custodian's own
 * are listed as chips with a × to drop them.
 */
import { useState } from 'react';
import { Alert, Badge, Button, CloseButton, Group, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

import type { MissingRate, RateSource } from '../api/portfolios';

/** A rate as people read it: "1 GBP = 1.152300 EUR". */
export const rateText = (ccy: string, eurPerUnit: string | number) =>
  `1 ${ccy} = ${Number(eurPerUnit).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 8 })} EUR`;

/** A substituted rate in words (the chip). */
export const sourceText = (s: RateSource) =>
  `${s.currency} for ${s.bank_mnemonic ?? 'No bank'} on ${s.date}: `
  + (s.source === 'last' ? `${s.bank_mnemonic ?? 'No bank'}'s rate of ${s.rate_date}`
    : s.source === 'other' ? `${s.from_bank_mnemonic ?? 'another custodian'}'s rate`
      : 'rate you entered')
  + ` · ${rateText(s.currency, s.rate)}`;

/** RATE-WHY (Pedro 2026-10-22: shade "for every [substituted rate], properly explained in each
 *  different case"): why a figure uses a rate other than its custodian's own, case by case. */
export const sourceWhy = (s: RateSource, what = 'This FX') => {
  const bank = s.bank_mnemonic ?? 'No bank';
  const rate = rateText(s.currency, s.rate);
  const why = `${bank} gives no ${s.currency} rate on ${s.date}`;
  if (s.source === 'last') return `${what} uses ${bank}'s last ${s.currency} rate, from ${s.rate_date} (${rate}): ${why}, and that earlier rate was chosen.`;
  if (s.source === 'other') return `${what} uses ${s.from_bank_mnemonic ?? 'another custodian'}'s ${s.currency} rate of ${s.date} (${rate}): ${why}, and another custodian's rate for the same day was chosen.`;
  return `${what} uses a ${s.currency} rate you entered (${rate}), for this view only: ${why}.`;
};

export function RateNotices({ missing, sources, leftOut, shown, onChoose }: {
  missing: MissingRate[]; sources: RateSource[]; leftOut: string[]; shown: string;
  onChoose: (key: string, choice: string | null) => void;
}) {
  const [typing, setTyping] = useState<string | null>(null);   // "bank:CCY" being typed
  const [typed, setTyped] = useState('');
  const typedOk = /^\d+([.,]\d+)?$/.test(typed.trim()) && Number(typed.replace(',', '.')) > 0;
  if (!missing.length && !sources.length && !leftOut.length) return null;
  return (
    <Stack gap={4}>
      {missing.map((m) => {
        const key = `${m.bank}:${m.currency}`;
        const bank = m.bank_mnemonic ?? 'No bank';
        return (
          <Alert key={key} color="yellow" py={6} icon={<IconAlertTriangle size={16} />}>
            <Stack gap={6}>
              <Text size="sm">
                <b>{bank}</b> gives no <b>{m.currency}</b> rate on <b>{m.date}</b>
                {m.portfolios.length ? <> (portfolio{m.portfolios.length > 1 ? 's' : ''} {m.portfolios.join(', ')})</> : null}.
                {' '}Until a rate is chosen, {m.portfolios.length > 1 ? 'they are' : 'it is'} left out of the totals and %.
              </Text>
              <Group gap="xs" wrap="wrap">
                {m.last && (
                  <Button size="compact-xs" variant="light" color="yellow" onClick={() => onChoose(key, 'last')}>
                    Use {bank}'s last rate ({m.last.date}): {rateText(m.currency, m.last.rate)}
                  </Button>
                )}
                {m.others.map((o) => (
                  <Button key={o.bank} size="compact-xs" variant="light" color="yellow" onClick={() => onChoose(key, `from:${o.bank}`)}>
                    Use {o.bank_mnemonic ?? 'No bank'}'s rate of {m.date}: {rateText(m.currency, o.rate)}
                  </Button>
                ))}
                {typing === key ? (
                  <Group gap={6} wrap="nowrap">
                    <Text size="xs">1 {m.currency} =</Text>
                    <TextInput size="xs" w={130} autoFocus placeholder="e.g. 1.1523" value={typed}
                      onChange={(e) => setTyped(e.currentTarget.value)}
                      error={typed.trim() && !typedOk ? 'a number above 0' : undefined} />
                    <Text size="xs">EUR</Text>
                    {typedOk && (
                      <Text size="xs" c="dimmed">(1 EUR = {(1 / Number(typed.replace(',', '.'))).toLocaleString(undefined, { maximumFractionDigits: 8 })} {m.currency})</Text>
                    )}
                    <Button size="compact-xs" disabled={!typedOk}
                      onClick={() => { onChoose(key, typed.trim().replace(',', '.')); setTyping(null); setTyped(''); }}>Use</Button>
                    <Button size="compact-xs" variant="default" onClick={() => { setTyping(null); setTyped(''); }}>Cancel</Button>
                  </Group>
                ) : (
                  <Tooltip withArrow multiline w={280} label="For this view only: the rate is not stored and goes when you reload or change the date.">
                    <Button size="compact-xs" variant="default" onClick={() => { setTyping(key); setTyped(''); }}>Enter a rate…</Button>
                  </Tooltip>
                )}
              </Group>
            </Stack>
          </Alert>
        );
      })}
      {leftOut.length > 0 && !missing.length && (
        <Alert color="yellow" py={6} icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">Left out of the totals and %, no {shown} figure for: <b>{leftOut.join(', ')}</b>.</Text>
        </Alert>
      )}
      {sources.length > 0 && (
        <Group gap={6} wrap="wrap">
          {sources.map((s) => (
            <Tooltip key={`${s.bank}:${s.currency}:${s.date}`} withArrow multiline w={360} label={sourceWhy(s, 'This view')}>
              <Badge color="yellow" variant="light" style={{ textTransform: 'none' }}
                rightSection={<CloseButton size="xs" variant="transparent" aria-label="Stop using this rate"
                  onClick={() => onChoose(`${s.bank}:${s.currency}`, null)} />}>
                {sourceText(s)}
              </Badge>
            </Tooltip>
          ))}
        </Group>
      )}
    </Stack>
  );
}
